import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from 'components/ui/carousel';
import { Check, AlertCircle, Mail } from 'lucide-react';
import axios from '../utils/axiosConfig';
import { toast } from 'hooks/use-toast';

interface SponsorshipPackage {
  id: number;
  name: string;
  points: number;
  price: number;
  discount: number;
  description: string;
}

const SponsorshipPage = () => {
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid' | 'not-found'>('idle');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [packages, setPackages] = useState<SponsorshipPackage[]>([]);
  const [loading, setLoading] = useState<number | null>(null);
  const [emailChecking, setEmailChecking] = useState(false);

  // Load packages from API
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get('/points-packages');
        setPackages(response.data.packages);
      } catch (error) {
        console.error('Error loading sponsorship packages:', error);
      }
    };

    fetchPackages();
  }, []);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const checkUserExists = async (email: string) => {
    if (emailChecking) return false; // Prevent multiple simultaneous requests
    
    setEmailChecking(true);
    try {
      const response = await axios.get(`/user/check/${email}`);
      return response.status === 200;
    } catch (error: any) {
      // Both rate limiting and user not found return 404
      // This prevents enumeration attacks
      if (error.response?.status === 404) {
        return false;
      }
      console.error('Error checking user:', error);
      return false;
    } finally {
      setEmailChecking(false);
    }
  };

  useEffect(() => {
    const handleEmailCheck = async () => {
      if (!email) {
        setEmailStatus('idle');
        setIsValidEmail(false);
        return;
      }

      if (!validateEmail(email)) {
        setEmailStatus('invalid');
        setIsValidEmail(false);
        return;
      }

      setEmailStatus('checking');
      const userExists = await checkUserExists(email);
      
      if (userExists) {
        setEmailStatus('valid');
        setIsValidEmail(true);
      } else {
        setEmailStatus('not-found');
        setIsValidEmail(false);
      }
    };

    const timeoutId = setTimeout(handleEmailCheck, 500);
    return () => clearTimeout(timeoutId);
  }, [email]);

  const handleManualEmailCheck = async () => {
    if (!email || !validateEmail(email) || emailChecking) return;
    
    setEmailStatus('checking');
    const userExists = await checkUserExists(email);
    
    if (userExists) {
      setEmailStatus('valid');
      setIsValidEmail(true);
    } else {
      setEmailStatus('not-found');
      setIsValidEmail(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleManualEmailCheck();
    }
  };

  const handleSponsor = async (packageItem: SponsorshipPackage) => {
    if (!isValidEmail) return;
    
    setLoading(packageItem.id);
    
    try {
      const response = await axios.get(`/sponsor/link/${packageItem.id}/email/${email}`);
      
      if (response.status === 200 && response.data.payment_url) {
        // Redirect to the payment URL
        window.location.href = response.data.payment_url;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Error creating sponsorship payment link:', error);
      
      // Show error message based on the error type
      if (error.response?.status === 404) {
        if (error.response.data.detail?.includes('User with email')) {
          toast({ 
            title: "Error", 
            description: "User not found. Please check the email address.", 
            variant: "destructive" 
          });
        } else {
          toast({ 
            title: "Error", 
            description: "Package not found. Please try again.", 
            variant: "destructive" 
          });
        }
      } else {
        toast({ 
          title: "Error", 
          description: "Failed to create sponsorship link. Please try again.", 
          variant: "destructive" 
        });
      }
    } finally {
      setLoading(null);
    }
  };

  const getEmailStatusIcon = () => {
    if (emailChecking || emailStatus === 'checking') {
      return <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />;
    }
    
    switch (emailStatus) {
      case 'valid':
        return <Check className="h-4 w-4 text-green-500" />;
      case 'invalid':
      case 'not-found':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Mail className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getEmailErrorMessage = () => {
    switch (emailStatus) {
      case 'invalid':
        return 'Invalid email format.';
      case 'not-found':
        return 'User not found.';
      default:
        return null;
    }
  };

  return (
      <div className="container mx-auto py-20 px-4 max-w-7xl">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Playfair_Display']">
            Multiply the Reach of the Gospel
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Support fellow believers by sponsoring their translation and voice generation needs. 
            Help quality evangelical content reach more languages worldwide and expand the Kingdom's reach.
          </p>
        </div>

        {/* Email Input Section */}
        <div className="max-w-md mx-auto mb-16">
          <div className="bg-muted rounded-xl p-6">
            <h2 className="text-xl font-semibold mb-4 text-center font-['Playfair_Display']">
              Who would you like to sponsor?
            </h2>
            <div className="relative">
              <Input
                type="email"
                placeholder="Enter their email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                className="pr-10"
                disabled={emailChecking}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {getEmailStatusIcon()}
              </div>
            </div>
            {getEmailErrorMessage() && (
              <p className="text-sm text-red-500 mt-2">{getEmailErrorMessage()}</p>
            )}
            {emailStatus === 'valid' && (
              <p className="text-sm text-green-600 mt-2">User found! Ready to sponsor.</p>
            )}
            {emailChecking && (
              <p className="text-sm text-blue-600 mt-2">Checking user...</p>
            )}
            {!email && (
              <p className="text-sm text-muted-foreground mt-2">Type email and press Enter to check</p>
            )}
          </div>
        </div>

        {/* Package Carousel */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8 font-['Playfair_Display']">
            Choose a Sponsorship Package
          </h2>
          
          <div className="relative max-w-6xl mx-auto">
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-2 md:-ml-4">
                {packages.map((pkg) => (
                  <CarouselItem key={pkg.id} className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3">
                    <Card className="h-full border-border">
                      <CardHeader className="text-center">
                        <CardTitle className="text-xl font-bold">{pkg.name}</CardTitle>
                        <CardDescription>{pkg.description}</CardDescription>
                      </CardHeader>
                      <CardContent className="text-center">
                        <div className="mb-4">
                          <span className="text-3xl font-bold">${pkg.price}</span>
                        </div>
                        <div className="text-lg font-semibold text-primary mb-4">
                          {pkg.points} points
                        </div>
                      </CardContent>
                      <CardFooter className="pt-0">
                        <Button 
                          className="w-full"
                          onClick={() => handleSponsor(pkg)}
                          disabled={!isValidEmail || loading !== null || emailChecking}
                        >
                          {loading === pkg.id ? (
                            <div className="flex items-center gap-2">
                              <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                              Processing...
                            </div>
                          ) : (
                            'Sponsor'
                          )}
                        </Button>
                      </CardFooter>
                    </Card>
                  </CarouselItem>
                ))}
              </CarouselContent>
                                      <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </div>

        {/* Closing Section */}
        <div className="text-center bg-muted rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-4 font-['Playfair_Display']">
            Your Sponsorship Makes a Difference
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Every sponsorship you provide helps break down language barriers and brings God's Word 
            to communities around the world. Together, we're using technology to fulfill the Great Commission 
            and ensure that every tribe and tongue can hear the Gospel in their heart language.
          </p>
        </div>
      </div>
  );
};

export default SponsorshipPage;